import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import type { ReactElement } from 'react';

import { Text } from '@/components/ui/text';
import { Tooltip, TooltipContent } from '@/components/ui/tooltip';

/**
 * A pause before the card opens, so crossing a list does not flash a card per row, and a short
 * one before it closes, so a pointer that slips off the edge does not lose it.
 */
const HOVER_OPEN_DELAY_MS = 400;

const HOVER_CLOSE_DELAY_MS = 100;

type QuestionGroupHoverCardProps = {
  /** Whether the trigger cut its text off. With nothing hidden there is no card. */
  enabled: boolean;
  /** The full text the trigger cut off. */
  text: string;
  /** Spread onto the one element that opens the card. On a phone it gets nothing. */
  children: (triggerProps: Record<string, unknown>) => ReactElement;
};

/**
 * Shows the full case line when the inquiry group card cuts it off, and nothing else: a card that
 * repeated what is already on screen would open for no reason. Expert rows have none; clicking a
 * row opens the thread, which shows everything. Phones have no hover, so the trigger renders alone
 * there.
 *
 * It is drawn as a hover card, not the dark tooltip: a light surface with the inquiry group card's
 * border, radius and type, so it reads as a larger view of the card.
 */
export function QuestionGroupHoverCard({ enabled, text, children }: QuestionGroupHoverCardProps) {
  if (!isWeb || !enabled) {
    return children({});
  }

  return (
    <Tooltip
      placement="bottom"
      openDelay={HOVER_OPEN_DELAY_MS}
      closeDelay={HOVER_CLOSE_DELAY_MS}
      className="h-auto w-auto"
      trigger={(triggerProps: Record<string, unknown>) => children(triggerProps)}>
      <TooltipContent className="max-w-[360px] rounded-[14px] border border-outline-100 bg-background-0 px-3.5 py-3 shadow-soft-2">
        <Text size="sm" className="text-typography-900">
          {text}
        </Text>
      </TooltipContent>
    </Tooltip>
  );
}
